import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMeasure } from 'react-use';
import { GAME_MODE, SingSetup, SongPreview } from '~/interfaces';
import { Calibration } from '~/modules/Calibration/Calibration';
import { Menu } from '~/modules/Elements/AKUI/Menu';
import { useBackground } from '~/modules/Elements/BackgroundContext';
import Modal from '~/modules/Elements/Modal';
import { VideoState } from '~/modules/Elements/VideoPlayer';
import GameState from '~/modules/GameEngine/GameState/GameState';
import events from '~/modules/GameEvents/GameEvents';
import PlayersManager from '~/modules/Players/PlayersManager';
import useSong from '~/modules/Songs/hooks/useSong';
import useBlockScroll from '~/modules/hooks/useBlockScroll';
import useViewportSize from '~/modules/hooks/useViewportSize';
import { CalibrationIntro } from '~/routes/Game/Singing/CalibrationIntro';
import WaitForReadiness from '~/routes/Game/Singing/WaitForReadiness';
import LayoutGame from '~/routes/LayoutGame';
import { IsCalibratedSetting, useSettingValue } from '~/routes/Settings/SettingsState';
import {
  SongListEntryDetailsArtist,
  SongListEntryDetailsTitle,
} from '~/routes/SingASong/SongSelection/Components/SongCard';
import Player, { PlayerRef } from './Player';
import PostGame from './PostGame/PostGame';

interface Props {
  singSetup: SingSetup;
  songPreview: SongPreview;
  returnToSongSelection: () => void;
  restartSong: () => void;
}
function Singing({ songPreview, singSetup, returnToSongSelection, restartSong }: Props) {
  useBlockScroll();
  const player = useRef<PlayerRef | null>(null);
  const song = useSong(songPreview.id);
  const [isCalibrated, setIsCalibrated] = useSettingValue(IsCalibratedSetting);

  const { width, height } = useViewportSize();
  const [stageRef, stageSize] = useMeasure<HTMLDivElement>();
  const [isEnded, setIsEnded] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const [showCalibrationIntro, setShowCalibrationIntro] = useState(true);
  const [playerState, setPlayerState] = useState(VideoState.UNSTARTED);
  const isOwnFullscreenElement = useCallback(() => {
    const fullscreenElement = document.fullscreenElement;

    return fullscreenElement === document.body || fullscreenElement === document.documentElement;
  }, []);
  const [isAppFullscreen, setIsAppFullscreen] = useState(isOwnFullscreenElement);

  const [isTransitionTimeout, setIsTransitionTimeout] = useState(false);

  useBackground(!isTransitionTimeout);

  useEffect(() => {
    const onFullscreenChange = () => setIsAppFullscreen(isOwnFullscreenElement());

    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [isOwnFullscreenElement]);

  const stageWidth = Math.floor(stageSize.width);
  const stageHeight = Math.floor(stageSize.height);
  const shouldRotatePlayer = isAppFullscreen && stageWidth > 0 && stageHeight > stageWidth && stageWidth < 760;
  const playerViewportWidth = shouldRotatePlayer ? stageHeight : stageWidth;
  const playerViewportHeight = shouldRotatePlayer ? stageWidth : stageHeight;
  const playerWidth =
    playerViewportWidth > 0 && playerViewportHeight > 0
      ? Math.min(playerViewportWidth, Math.floor(playerViewportHeight * (16 / 9)))
      : 0;
  const playerHeight = playerWidth > 0 ? Math.min(playerViewportHeight, Math.floor(playerWidth * (9 / 16))) : 0;

  const showCalibration = !isCalibrated;
  useEffect(() => {
    if (
      !showCalibration &&
      isOverlayVisible &&
      song.data &&
      (isTransitionTimeout || playerState !== VideoState.UNSTARTED)
    ) {
      setIsOverlayVisible(false);
    }
  }, [song.data, isTransitionTimeout, playerState, isOverlayVisible]);

  if (isEnded && song.data) {
    return (
      <PostGame
        width={stageWidth || width}
        height={stageHeight || height}
        song={song.data}
        onClickSongSelection={returnToSongSelection}
        singSetup={singSetup}
      />
    );
  } else {
    return (
      <LayoutGame>
        <Stage ref={stageRef}>
          {playerWidth > 0 && playerHeight > 0 && (
            <Container
              data-rotated={shouldRotatePlayer || undefined}
              style={{ width: playerWidth, height: playerHeight }}>
              <BackgroundContainer visible={isOverlayVisible} data-test="background-container">
                <Overlay video={songPreview.video} width={playerWidth} height={playerHeight} />
                <Artist data-test="song-artist">{songPreview.artist}</Artist>
                <Title data-test="song-title">{songPreview.title}</Title>
                <Modal open={showCalibration}>
                  {showCalibration && (
                    <Menu>
                      {showCalibrationIntro ? (
                        <CalibrationIntro onContinue={() => setShowCalibrationIntro(false)} />
                      ) : (
                        <Calibration onSave={() => setIsCalibrated(true)} />
                      )}
                    </Menu>
                  )}
                </Modal>
                {!showCalibration && (
                  <WaitForReadiness
                    onFinish={() => {
                      setIsTransitionTimeout(true);
                      player.current?.play();
                    }}
                  />
                )}
              </BackgroundContainer>
              {song.data && !showCalibration && (
                <Player
                  pauseMenu
                  ref={player}
                  onStatusChange={setPlayerState}
                  song={song.data}
                  width={playerWidth}
                  height={playerHeight}
                  autoplay={false}
                  onSongEnd={() => {
                    const scores =
                      GameState.getSingSetup()?.mode === GAME_MODE.CO_OP
                        ? [
                            {
                              name: PlayersManager.getPlayers()
                                .map((player) => player.getName())
                                .join(', '),
                              score: GameState.getPlayerScore(0),
                            },
                          ]
                        : PlayersManager.getPlayers().map((player) => ({
                            name: player.getName(),
                            score: GameState.getPlayerScore(player.number),
                          }));
                    const progress = GameState.getSongCompletionProgress();
                    events.songEnded.dispatch(song.data!, singSetup, scores, progress);
                    setIsEnded(true);
                  }}
                  singSetup={singSetup}
                  restartSong={restartSong}
                />
              )}
            </Container>
          )}
        </Stage>
      </LayoutGame>
    );
  }
}

const Stage = styled.div`
  width: 100%;
  height: var(--karaoke-content-height, 100vh);
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #030b2c;
`;

const Container = styled.div`
  position: relative;
  background: black;
  overflow: hidden;
  transform-origin: center center;

  &[data-rotated='true'] {
    transform: rotate(90deg);
  }
`;

const BackgroundContainer = styled.div<{ visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 10;
  pointer-events: none;
  background-color: black;
  view-transition-name: song-preview;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: 500ms;
`;

const BaseOverlay = styled.div`
  background-size: cover;
  background-position: center center;
  filter: blur(10px);
`;

const Overlay = (props: { video: string; width: number; height: number }) => (
  <BaseOverlay
    style={{
      backgroundImage: `url('https://i3.ytimg.com/vi/${props.video}/hqdefault.jpg')`,
      width: `${props.width}px`,
      height: `${props.height}px`,
    }}
  />
);

const Artist = styled(SongListEntryDetailsArtist)`
  view-transition-name: song-preview-artist;
  position: absolute;
  top: 10rem;
  left: 10rem;
  font-size: 7rem;
`;
const Title = styled(SongListEntryDetailsTitle)`
  view-transition-name: song-preview-title;
  position: absolute;

  font-size: 8rem;
  top: 19rem;
  left: 10rem;
`;

export default Singing;
